import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { Plus, Minus, Check } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { SERVICES } from "../data/content";

function ServiceCard({ service, index }) {
  const [open, setOpen] = useState(false);
  const Icon = Icons[service.icon] || Icons.Sparkles;
  const preview = service.items.slice(0, 4);

  return (
    <Reveal delay={index * 0.08} className="h-full">
      <motion.div
        data-testid={`service-card-${service.id}`}
        whileHover={{ y: -8 }}
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
        className="group glass rounded-3xl overflow-hidden h-full flex flex-col"
      >
        {/* Image header */}
        <div className="relative h-44 overflow-hidden">
          <img
            src={service.image}
            alt={service.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-jo-primary/85 via-jo-primary/30 to-transparent" />
          <div className="absolute top-4 left-4 h-12 w-12 rounded-2xl bg-white/90 backdrop-blur flex items-center justify-center shadow-lg">
            <Icon size={22} className="text-jo-primary" strokeWidth={1.75} />
          </div>
          <span className="absolute top-5 right-4 text-[10px] font-semibold tracking-widest uppercase text-white/90 bg-jo-highlight/90 px-3 py-1 rounded-full">
            {service.tag}
          </span>
          <h3 className="absolute bottom-4 left-4 right-4 text-xl font-semibold text-white leading-snug">
            {service.title}
          </h3>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col flex-1">
          <p className="text-sm text-slate-600 font-light leading-relaxed">{service.desc}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            {(open ? service.items : preview).map((item) => (
              <span
                key={item}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-jo-primary bg-jo-accent/10 rounded-full px-3 py-1.5"
              >
                <Check size={11} className="text-jo-accent" strokeWidth={3} />
                {item}
              </span>
            ))}
          </div>

          {service.items.length > 4 && (
            <button
              data-testid={`service-toggle-${service.id}`}
              onClick={() => setOpen((o) => !o)}
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-jo-accent hover:text-jo-highlight transition-colors self-start"
            >
              {open ? <Minus size={15} /> : <Plus size={15} />}
              {open ? "Show less" : `+${service.items.length - 4} more services`}
            </button>
          )}
        </div>
      </motion.div>
    </Reveal>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <SectionHeader
          center
          label="Our Services"
          title="End-to-end solutions built for scale"
          desc="Premium outsourcing and technology services engineered to improve efficiency, reduce operational costs and accelerate growth."
        />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {SERVICES.map((service, i) => (
            <ServiceCard key={service.id} service={service} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
